import React, { useMemo } from 'react';

import { Flex } from 'web-styled';

import * as S from './styled';

export interface TabPaneProps {
  value: string;
  label: React.ReactNode;
  children?: React.ReactNode;
}

type TabSize = 'small' | 'medium' | 'large';

interface TabsProps {
  size?: TabSize;
  active: string;
  onRequestChange: (value: string) => void;
  children: React.ReactElement<TabPaneProps> | Array<React.ReactElement<TabPaneProps>>;
}

const isSameTab = (a?: string, b?: string) => a?.toLowerCase() === b?.toLowerCase();

const Tabs = ({ size = 'small', active, children, onRequestChange }: TabsProps) => {
  const panes = useMemo(
    () =>
      React.Children.toArray(children).filter(
        (child): child is React.ReactElement<TabPaneProps> => React.isValidElement(child),
      ),
    [children],
  );

  if (!panes.length) return null;

  const activePane = panes.find(({ props }) => isSameTab(props.value, active));

  return (
    <Flex column width="100%" spacing={10}>
      <Flex width="100%" spacing={15} noPrint role="tablist">
        {panes.map(({ props }) => (
          <S.Tab
            key={props.value}
            role="tab"
            aria-selected={isSameTab(props.value, active)}
            size={size}
            active={isSameTab(props.value, active)}
            onClick={() => onRequestChange(props.value)}
          >
            {props.label}
          </S.Tab>
        ))}
      </Flex>
      <Flex width="100%">{activePane}</Flex>
    </Flex>
  );
};

export default Tabs;
